/**
 * Ranked-matchmaking search overlay (Phase K).
 *
 * Mounted full-screen when the user clicks "Lancer la recherche" in the
 * lobby's ranked modal. Joins the singleton `ranked_mm` Colyseus room,
 * tells it the desired match size, then waits for a `match.found`
 * message containing the new ranked room's id — at which point we
 * leave the matchmaking room and `joinById` the actual game.
 *
 * Visuals: a Solari split-flap-style board of iconchats up top + a row
 * of "flipping" text plates that rotate every ~3 s through funny
 * cat-themed waiting messages. Cancel button leaves the queue and
 * dismisses the overlay.
 */

import { colyseusClient } from '../lobby/colyseus.js';
import type { Router } from '../router.js';

const SEARCH_LINES = [
  'Renifle les souris ennemies…',
  'Aiguise les griffes de la patrouille…',
  'Compte les croquettes du concurrent…',
  'Réveille les chats endormis…',
  'Étire 7 pattes du dos…',
  'Cherche des aristos pour la baston…',
  'Astiquage des moustaches en cours…',
  'Mesure le diamètre des coussinets…',
  'Frotte le pelage des prétendants…',
  'Compte les abonnés du gros chat…',
];

const HEAD_ICONS = [
  '/sprites/iconchat_guerrier.png',
  '/sprites/iconchat_archer.png',
  '/sprites/iconchat_lancier.png',
  '/sprites/iconchat_paladin.png',
  '/sprites/iconchat_champion.png',
  '/sprites/iconchat_magicien.png',
  '/sprites/iconchat_barbare.png',
];

interface MmRoom {
  send: (type: string, payload?: unknown) => void;
  onMessage: (type: string, fn: (payload: unknown) => void) => void;
  leave: () => Promise<number>;
  state: { pools?: ArrayLike<{ size: number; waiting: number }> };
  onStateChange: ((fn: () => void) => void) & { remove: (fn: () => void) => void };
}

export function startRankedMatchmaking(router: Router, size: number): void {
  // Build the overlay first so the user sees feedback while we wait
  // for the matchmaking room to respond.
  document.querySelectorAll('.ranked-search-overlay').forEach((n) => n.remove());
  const overlay = document.createElement('div');
  overlay.className = 'ranked-search-overlay';

  // Top row — iconchats that "flip" through different heads.
  const heads = document.createElement('div');
  heads.className = 'ranked-search-heads';
  const headCells: HTMLImageElement[] = [];
  for (let i = 0; i < 5; i++) {
    const wrap = document.createElement('div');
    wrap.className = 'ranked-search-head-cell';
    const img = document.createElement('img');
    img.src = HEAD_ICONS[(i * 2) % HEAD_ICONS.length]!;
    img.className = 'ranked-search-head';
    img.alt = '';
    wrap.appendChild(img);
    heads.appendChild(wrap);
    headCells.push(img);
  }
  overlay.appendChild(heads);

  // Title + waiting status.
  const title = document.createElement('h1');
  title.className = 'ranked-search-title';
  title.textContent = `Recherche d'une partie ${size} joueurs…`;
  overlay.appendChild(title);

  const status = document.createElement('div');
  status.className = 'ranked-search-status';
  status.textContent = '1 chaton en file…';
  overlay.appendChild(status);

  // The "flip plate" line — text rotates every ~3 s with a vertical
  // CSS flip animation.
  const plate = document.createElement('div');
  plate.className = 'ranked-search-plate';
  const plateFront = document.createElement('span');
  plateFront.className = 'ranked-search-plate-text';
  plateFront.textContent = SEARCH_LINES[0]!;
  plate.appendChild(plateFront);
  overlay.appendChild(plate);

  // Cancel button — leaves the queue + dismisses the overlay.
  const cancelBtn = document.createElement('button');
  cancelBtn.type = 'button';
  cancelBtn.className = 'btn-secondary ranked-search-cancel';
  cancelBtn.textContent = 'Annuler la recherche';
  overlay.appendChild(cancelBtn);

  document.body.appendChild(overlay);

  // Cycle the plate text every 3 s with the flip animation.
  let lineIndex = 0;
  const plateInterval = window.setInterval(() => {
    lineIndex = (lineIndex + 1) % SEARCH_LINES.length;
    plate.classList.add('is-flipping');
    window.setTimeout(() => {
      plateFront.textContent = SEARCH_LINES[lineIndex]!;
      plate.classList.remove('is-flipping');
    }, 220);
  }, 3000);

  // Cycle the head sprites every 1.2 s for visual life.
  const headInterval = window.setInterval(() => {
    for (let i = 0; i < headCells.length; i++) {
      const img = headCells[i]!;
      const next = HEAD_ICONS[Math.floor(Math.random() * HEAD_ICONS.length)]!;
      img.src = next;
    }
  }, 1200);

  let mmRoom: MmRoom | null = null;
  let cancelled = false;

  const cleanup = (): void => {
    cancelled = true;
    window.clearInterval(plateInterval);
    window.clearInterval(headInterval);
    overlay.remove();
    if (mmRoom) {
      try { mmRoom.send('queue.cancel'); } catch { /* room may already be closing */ }
      void mmRoom.leave().catch(() => null);
      mmRoom = null;
    }
  };
  cancelBtn.addEventListener('click', cleanup);

  // Join the singleton matchmaking room and request our chosen size.
  void (async () => {
    try {
      const room = (await colyseusClient.joinOrCreate('ranked_mm')) as unknown as MmRoom;
      if (cancelled) {
        void room.leave().catch(() => null);
        return;
      }
      mmRoom = room;
      room.send('queue.set', { size });

      const updateStatus = (): void => {
        const pools = room.state.pools;
        if (!pools) return;
        for (let i = 0; i < pools.length; i++) {
          const pe = pools[i]!;
          if (pe.size === size) {
            const w = pe.waiting;
            status.textContent = `${w} chaton${w > 1 ? 's' : ''} en file pour ${size} joueurs…`;
            return;
          }
        }
      };
      room.onStateChange(updateStatus);
      updateStatus();

      room.onMessage('match.found', (payload: unknown) => {
        const p = payload as { roomId?: string } | null;
        if (!p?.roomId) return;
        cancelled = true;
        window.clearInterval(plateInterval);
        window.clearInterval(headInterval);
        overlay.remove();
        // Leave the matchmaking room (best-effort), then navigate to the
        // ranked game room. The room screen will pick up the existing
        // session and call joinById on its own.
        void room.leave().catch(() => null);
        router.navigate(`/room/${p.roomId}`);
      });
    } catch (err) {
      console.error('[matchmaking] failed to join queue', err);
      status.textContent = 'Impossible de rejoindre la file. Réessaie dans un instant.';
    }
  })();
}
